import { Controller } from "@hotwired/stimulus"

// Docs inline-YouTube: turns every `<a href="https://youtu.be/..."><img/></a>`
// thumbnail in the article into:
//
//   1. a stamped thumbnail with the standard YouTube play-button overlay so
//      it visually reads as "this is a video, click to play";
//   2. a click target that opens an inline lightbox with a youtube-nocookie
//      embed (autoplay) instead of navigating away to a new tab.
//
// Wired from `<article>` in show.html.erb via
// `data-controller="docs--lightbox docs--youtube"`.
//
// The Sanitize allowlist in MarkdownRenderer strips `data-controller` from
// markdown content, which is why we need a single wrapper controller on the
// article element rather than per-anchor wiring in the .md sources.
export default class extends Controller {
  static OVERLAY_ID = "docs-youtube-overlay"

  // Inline SVG of the YouTube play badge — gradient red rounded rectangle
  // with a centered white triangle. Positioned via parent's CSS.
  static PLAY_BADGE = `
    <span class="docs-yt-play" aria-hidden="true">
      <svg viewBox="0 0 68 48" xmlns="http://www.w3.org/2000/svg">
        <path d="M66.52 7.74a8.13 8.13 0 0 0-5.72-5.74C55.85 1 34 1 34 1S12.15 1 7.2 2a8.13 8.13 0 0 0-5.72 5.74A85.62 85.62 0 0 0 0 24a85.62 85.62 0 0 0 1.48 16.26 8.13 8.13 0 0 0 5.72 5.74C12.15 47 34 47 34 47s21.85 0 26.8-1a8.13 8.13 0 0 0 5.72-5.74A85.62 85.62 0 0 0 68 24a85.62 85.62 0 0 0-1.48-16.26z" fill="#f00" />
        <path d="M27 34l18-10-18-10z" fill="#fff" />
      </svg>
    </span>
  `

  connect() {
    this._onClick = this._onClick.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    this._stamped = new WeakSet()
    this._stamp()
    document.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    this._close()
    document.removeEventListener("keydown", this._onKeydown)
    this.element.querySelectorAll(this._anchorSelector()).forEach((a) => {
      a.removeEventListener("click", this._onClick)
    })
  }

  // -- private --

  _anchorSelector() {
    // Match every YouTube link variant the importer might produce.
    return [
      "a[href^='https://youtu.be/']",
      "a[href^='http://youtu.be/']",
      "a[href*='youtube.com/watch']",
      "a[href*='youtube-nocookie.com/embed']",
    ].join(", ")
  }

  _stamp() {
    const anchors = this.element.querySelectorAll(this._anchorSelector())
    anchors.forEach((a) => {
      if (this._stamped.has(a)) return
      this._stamped.add(a)

      const id = this._extractId(a.href)
      if (!id) return

      // Visual: position-relative wrapper + play badge overlay. The
      // existing markup is `<a><img/></a>`, so we add a span sibling,
      // promote the anchor to position:relative + display:inline-block.
      a.classList.add("docs-yt-thumb")
      // Avoid double-stamping the play badge if the controller reconnects.
      if (!a.querySelector(".docs-yt-play")) {
        a.insertAdjacentHTML("beforeend", this.constructor.PLAY_BADGE)
      }

      a.addEventListener("click", this._onClick)
    })
  }

  _extractId(href) {
    try {
      const url = new URL(href)
      // youtu.be/<id>
      if (url.hostname === "youtu.be") {
        return url.pathname.slice(1).split("/")[0] || null
      }
      // youtube.com/watch?v=<id>
      if (url.hostname.endsWith("youtube.com")) {
        const v = url.searchParams.get("v")
        if (v) return v
        // youtube.com/embed/<id>
        const m = url.pathname.match(/\/embed\/([^/?]+)/)
        if (m) return m[1]
      }
      // youtube-nocookie.com/embed/<id>
      if (url.hostname.endsWith("youtube-nocookie.com")) {
        const m = url.pathname.match(/\/embed\/([^/?]+)/)
        if (m) return m[1]
      }
    } catch (_) {
      /* invalid URL — fall through */
    }
    return null
  }

  _onClick(event) {
    const a = event.currentTarget
    const id = this._extractId(a.href)
    if (!id) return // unknown shape — let the anchor open in a new tab
    event.preventDefault()
    this._open(id, a.getAttribute("aria-label") || a.querySelector("img")?.alt)
  }

  _onKeydown(event) {
    if (event.key === "Escape") this._close()
  }

  _open(id, label) {
    this._close()
    const overlay = document.createElement("div")
    overlay.id = this.constructor.OVERLAY_ID
    overlay.setAttribute("role", "dialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.setAttribute("aria-label", label || "YouTube video")
    // Use inline styles — this element is created dynamically in JS and
    // Tailwind's scanner does not process app/javascript/ files, so arbitrary
    // classes like z-[100], bg-black/85, aspect-video, border-white/10 are
    // absent from the CSS build and silently no-op.
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:100;display:flex;align-items:center;" +
      "justify-content:center;background-color:rgba(0,0,0,0.85);padding:1rem;" +
      "backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);cursor:zoom-out"
    overlay.addEventListener("click", () => this._close())

    const wrap = document.createElement("div")
    wrap.style.cssText =
      "position:relative;width:100%;max-width:56rem;aspect-ratio:16/9;" +
      "border-radius:0.5rem;overflow:hidden;background-color:#000;" +
      "box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);" +
      "border:1px solid rgba(255,255,255,0.1)"
    wrap.addEventListener("click", (ev) => ev.stopPropagation())

    const iframe = document.createElement("iframe")
    iframe.style.cssText = "position:absolute;inset:0;width:100%;height:100%"
    iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`
    iframe.title = label || "YouTube video player"
    iframe.allow =
      "accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    iframe.allowFullscreen = true
    iframe.referrerPolicy = "strict-origin-when-cross-origin"
    wrap.appendChild(iframe)

    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.setAttribute("aria-label", "Close video")
    closeBtn.style.cssText =
      "position:absolute;top:-3rem;right:0;display:flex;height:2.25rem;width:2.25rem;" +
      "align-items:center;justify-content:center;border-radius:9999px;" +
      "background-color:rgba(255,255,255,0.1);color:#fff;border:none;cursor:pointer;" +
      "outline:none;font-size:1rem;line-height:1;transition:background-color 0.15s"
    closeBtn.textContent = "✕"
    closeBtn.addEventListener("mouseenter", () => {
      closeBtn.style.backgroundColor = "rgba(255,255,255,0.2)"
    })
    closeBtn.addEventListener("mouseleave", () => {
      closeBtn.style.backgroundColor = "rgba(255,255,255,0.1)"
    })
    closeBtn.addEventListener("click", (ev) => {
      ev.stopPropagation()
      this._close()
    })
    wrap.appendChild(closeBtn)

    overlay.appendChild(wrap)
    document.body.appendChild(overlay)
    document.body.style.overflow = "hidden"
    closeBtn.focus()
  }

  _close() {
    const existing = document.getElementById(this.constructor.OVERLAY_ID)
    if (existing) existing.remove()
    document.body.style.overflow = ""
  }
}
